import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Callout, Card, Heading, Text } from '../ui/index.js';
import { useQueryClient } from '@tanstack/react-query';
import { signIn } from '../auth/session.js';
import { Focusable } from '../components/focus/Focusable.js';
import { useAccess, keys } from '../api/hooks.js';
import { errorMessage } from '../lib/errors.js';
import { isNative } from '../platform/index.js';
import { track } from '../analytics/client.js';

export const Route = createFileRoute('/signin')({
  component: SignIn,
  validateSearch: (search: Record<string, unknown>): { error?: string; return_to?: string } => ({
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
    ...(typeof search.return_to === 'string' ? { return_to: search.return_to } : {}),
  }),
});

function SignIn() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const { signedIn } = useAccess();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(
    search.error ? 'Signing in was cancelled.' : null,
  );

  React.useEffect(() => {
    if (signedIn) void navigate({ to: search.return_to ?? '/', replace: true });
  }, [signedIn, navigate, search.return_to]);

  const start = async () => {
    setBusy(true);
    setError(null);
    // Before the round trip, not after: the whole point of the pair is to see how many people
    // start signing in and never come back, which is a number only the first half can carry.
    track('signin_started');
    try {
      await signIn(search.return_to ?? '/');
      // The apps come back here once the browser round trip finishes.
      await qc.invalidateQueries({ queryKey: keys.me });
      track('signin_completed');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gb-center">
      <Card size="4">
        <Heading size="6">{'Sign in'}</Heading>
        <Text render={<p />} color="gray">
          {'GOOD BUILD uses Discord for sign in.'}
        </Text>

        {error ? (
          <Callout.Root color="danger">
            <Callout.Description>{error}</Callout.Description>
          </Callout.Root>
        ) : null}

        {busy && isNative ? (
          <Text render={<p />} size="2" color="gray">
            {'Finish signing in there, then come back to the app.'}
          </Text>
        ) : null}

        <Focusable
          autoFocus
          onSelect={() => void start()}
          selectLabel={'Sign in with Discord'}
          disabled={busy}
        >
          <Button size="4" disabled={busy}>
            {busy ? 'Opening your browser' : 'Sign in with Discord'}
          </Button>
        </Focusable>
      </Card>
    </div>
  );
}
